import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useState } from "react";
import {
    addNewVariable,
    deleteVariable,
    updateVariable
} from "../../services/variables";
import ActionBody from "./components/ActionBody";
import RoundedButton from "../buttons/RoundedButton";
import FormDialog from "../dialog/FormDialog";
import { TbSettingsOff } from "react-icons/tb";
import InfoSection from "../info/InfoSection";
import { BiHelpCircle } from "react-icons/bi";
import TextAreaWithCounter from "../inputs/TextAreaWithCounter";
import Select from "react-select";
import ConfirmDialog from "../dialog/ConfirmDialog";
import { MdDelete } from "react-icons/md";
import CollapsibleSection from "../CollapsibleSection";
import NoDataMsg from "../info/NoDataMsg";
import { TbCopyCheckFilled } from "react-icons/tb";
import AlertMsg from "../info/AlertMsg";
import { useNavigate } from "react-router-dom";

const SETTINGS_LABELS = {
    is_required: "Requerido",
    is_editable: "Editable",
    is_deletable: "Eliminable",
    is_persistent: "Persistente",
};

const SETTINGS_OPTIONS = [
    { key: "is_required", label: "Requerido" },
    { key: "is_editable", label: "Editable" },
    { key: "is_deletable", label: "Eliminable" },
    { key: "is_persistent", label: "Persistente" },
];

const SETTINGS_HINTS = {
    is_required: "El usuario debe completarla sí o sí.",
    is_editable: "Permite modificar el valor luego.",
    is_deletable: "Permite eliminar el dato guardado.",
    is_persistent: "Se conserva entre conversaciones.",
};

const emptyVariable = {
    description: "",
    entity_id: null,
    settings: {
        is_required: false,
        is_deletable: false,
        is_editable: false,
        is_persistent: false
    }
}

function TableVariables({ variables, setVariables, slotsCandidates }) {
    const [variableDialog, setVariableDialog] = useState(false);
    const [deleteVariableDialog, setDeleteVariableDialog] = useState(false);
    const [selectedVariable, setSelectedVariable] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [settingsInfoDialog, setSettingsInfoDialog] = useState(false);
    const [variable, setVariable] = useState(emptyVariable);

    const navigate = useNavigate();

    const candidateOptions = (slotsCandidates || []).map((candidate) => ({
        value: candidate.id,
        label: candidate.title || candidate.name || `Entidad ${candidate.id}`,
    }));

    const selectedCandidateOption = candidateOptions.find(
        (option) => option.value === variable.entity_id
    ) || null;

    const handleSelectCandidate = (selectedOption) => {
        setVariable((prev) => ({
            ...prev,
            entity_id: selectedOption?.value ?? null,
        }));
    };

    const openEdit = (rowData) => {
        setVariable({
            description: rowData.description || "",
            entity_id: rowData.entity_id ?? rowData.entity_id ?? null,
            settings: {
                is_required: rowData.is_required,
                is_editable: rowData.is_editable,
                is_deletable: rowData.is_deletable,
                is_persistent: rowData.is_persistent,
            }
        });
        setSelectedVariable(rowData);
        setIsEditMode(true);
        setVariableDialog(true);
    };

    const handleSettingToggle = (settingKey) => {
        setVariable(prev => ({
            ...prev,
            settings: {
                ...prev.settings,
                [settingKey]: !prev.settings[settingKey],
            }
        }));
    };

    const openNew = () => {
        setIsEditMode(false);
        setSelectedVariable(null);
        setVariable(emptyVariable);
        setVariableDialog(true);
    };

    const closeDialog = () => {
        setVariableDialog(false);
        setIsEditMode(false);
        setSelectedVariable(null);
        setVariable(emptyVariable);
    };

    const openDeleteDialog = (rowData) => {
        setSelectedVariable(rowData);
        setDeleteVariableDialog(true);
    };

    const deleteSelectedVariable = async () => {
        if (!selectedVariable) return;

        setDeleteVariableDialog(false);
        const res = await deleteVariable(selectedVariable.id);

        if (res) {
            setSelectedVariable(null);
            setVariables((prevVariables) =>
                prevVariables.filter((v) => v.id !== selectedVariable.id)
            );
        }
    };

    const saveVariable = async () => {
        let res;

        if (isEditMode && selectedVariable) {
            res = await updateVariable(selectedVariable.id, variable);
            if (res) {
                setVariables((prevVariables) =>
                    prevVariables.map((v) =>
                        v.id === selectedVariable.id ? {
                            ...v,
                            description: variable.description,
                            entity_id: variable.entity_id,
                            is_required: variable.settings.is_required,
                            is_editable: variable.settings.is_editable,
                            is_deletable: variable.settings.is_deletable,
                            is_persistent: variable.settings.is_persistent,
                        } : v
                    )
                );
            }
        } else {
            res = await addNewVariable(variable);
            if (res) {
                setVariables((prevVariables) => [...prevVariables, res.new_slot]);
            }
        }

        if (res) {
            setVariableDialog(false);
            setIsEditMode(false);
            setSelectedVariable(null);
            setVariable(emptyVariable);
        }
    };

    return (
        <>
            {variables && variables.length > 0 ? (
                <div
                    className="card p-0" aria-label="table"
                    style={{
                        borderRadius: "12px",
                        overflow: "hidden",
                    }}
                >
                    <DataTable
                        value={variables}
                        dataKey="id"
                        paginator
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        filterDisplay="row"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                        filter
                        responsiveLayout="scroll"
                    >

                        <Column
                            field={"label"}
                            header={"Variable"}
                            filter
                            filterMatchMode={["contains"]}
                            showFilterMenu={false}
                            sortable
                            body={(rowData) => `${rowData.label || rowData.slot_name}`}
                        />

                        <Column
                            field={"description"}
                            header={"Descripcion"}
                            showFilterMenu={false}
                            body={(rowData) => {
                                return rowData.description ? (
                                    rowData.description
                                ) : (
                                    <span
                                        style={{
                                            color: "#9ca3af",
                                            fontSize: "14px"
                                        }}
                                    >
                                        - Sin descripción -
                                    </span>
                                );
                            }}
                        />

                        <Column
                            header={
                                <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <span>Configuración</span>
                                    <BiHelpCircle
                                        size={22}
                                        onClick={() => setSettingsInfoDialog(true)}
                                        style={{
                                            color: "#2f3e9eff",
                                            marginLeft: "8px",
                                            cursor: "pointer"
                                        }} />
                                </div>
                            }
                            body={(rowData) => {
                                const enabled = Object.entries(SETTINGS_LABELS)
                                    .filter(([key]) => rowData[key] === true);

                                if (enabled.length === 0) {
                                    return (
                                        <span
                                            className="ms-2 d-inline-flex align-items-center px-2 py-1"
                                            style={{
                                                color: "#9ca3af",
                                                fontSize: "14px"
                                            }}
                                        >
                                            <TbSettingsOff style={{ color: "#9ca3af", marginRight: "8px" }} />
                                            Sin configurar
                                        </span>
                                    );
                                }

                                return (
                                    <div
                                        style={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "0.5rem",
                                        }}
                                    >
                                        {enabled.map(([key, label]) => (
                                            <div
                                                key={"setting-" + key}
                                                className="entity-value-chip"
                                                title={label}
                                            >
                                                <span
                                                    className="entity-value-chip-text"
                                                    key={key}
                                                >
                                                    {label}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                );
                            }}
                        />

                        <Column
                            field={"used_in_forms"}
                            header={"Usos"}
                            body={(rowData) => {
                                return rowData?.forms?.map((form) => (
                                    <div
                                        key={"setting-" + form.id}
                                        className="entity-value-chip warning"
                                        title={form.title}
                                        onClick={() => { navigate(`/forms/branches/${form.id}`); }}
                                        style={{ cursor: "pointer" }}
                                    >
                                        <span
                                            className="entity-value-chip-text warning"
                                            key={form.id}
                                        >Formulario: {form.title}</span>
                                    </div>
                                ));
                            }}
                        />
                        <Column
                            header={<RoundedButton label="+ Agregar" handleClick={openNew} />}
                            className={"actionheader"}
                            body={(rowData) => {
                                return (
                                    rowData?.type !== "system" ? (
                                        <ActionBody
                                            data={rowData}
                                            toggleDelete={() => openDeleteDialog(rowData)}
                                            toggleEdit={() => openEdit(rowData)}
                                            editB={true}
                                            deleteB={true}
                                        />
                                    ) : null
                                );
                            }}
                            exportable={false}
                            style={{ minWidth: ".2rem" }}
                        ></Column>
                    </DataTable>
                </div >
            ) : (
                <div>
                    <NoDataMsg
                        icon={<TbCopyCheckFilled />}
                        title="Sin variables"
                        subtitle="Aún no has creado ninguna variable. Crea una para poder usarla en tus conversaciones."
                        buttons={[
                            <RoundedButton
                                label="Crear Variable"
                                color="trini"
                                handleClick={() => {
                                    setVariableDialog(true);
                                }}
                            />
                        ]}
                    />
                </div>
            )}

            <FormDialog
                visible={variableDialog}
                title={isEditMode ? "Editar Variable" : "Nueva Variable"}
                closeDialog={closeDialog}
                acceptDialog={saveVariable}
            >
                <div className="mb-3">
                    <InfoSection title={"¿Qué son las variables en Trini?"}>
                        <div style={{ fontSize: "14px" }}>
                            Las variables permiten al chatbot <strong>recordar información importante durante la conversación</strong>.
                            Funcionan como una memoria donde se almacenan datos que el usuario va proporcionando,
                            como su nombre, número de cliente u otros datos relevantes.
                            <br /><br />
                            Esta información puede reutilizarse para ofrecer respuestas personalizadas,
                            evitar repetir preguntas y mejorar la experiencia general del usuario.
                        </div>
                    </InfoSection>
                </div>

                {!isEditMode && (
                    <div className="mb-3">
                        <AlertMsg text={"Solo aquellas <strong>entidades que son parte de un formulario</strong> pueden configurarse como variables."} />
                    </div>
                )}

                <div className="d-flex flex-column gap-3">
                    {!isEditMode && (
                        <div className="field">
                            <label className={"ms-3"}>
                                Entidad
                            </label>
                            <Select
                                classNamePrefix="react-select"
                                options={candidateOptions}
                                value={selectedCandidateOption}
                                menuPosition="fixed"
                                onChange={handleSelectCandidate}
                                noOptionsMessage={() => "No se ha registrado ninguna entidad."}
                                placeholder="Selecciona una entidad.."
                            />
                        </div>)}

                    <div className="field">
                        <label className={"ms-3"}>
                            Descripción
                        </label>
                        <TextAreaWithCounter
                            value={variable.description ?? ""}
                            onChange={(e) => {
                                setVariable((prev) => ({
                                    ...prev,
                                    description: e.target.value,
                                }));
                            }}
                            required
                            maxLength={300}
                            placeholder="Ingresa una descripción.."
                        />
                    </div>

                    <div className="field">
                        <CollapsibleSection
                            title="Opciones de configuración"
                            initialShowState={true}
                        >
                            <div className="d-flex flex-wrap gap-2 mt-3 mb-2">
                                {SETTINGS_OPTIONS.map((option) => {
                                    const isSelected = Boolean(variable.settings[option.key]);

                                    return (
                                        <button
                                            type="button"
                                            key={option.key}
                                            onClick={() => handleSettingToggle(option.key)}
                                            className="text-start border"
                                            style={{
                                                minWidth: "220px",
                                                flex: "1 1 220px",
                                                borderRadius: "12px",
                                                padding: "10px 12px",
                                                transition: "all .15s ease",
                                                backgroundColor: isSelected
                                                    ? "var(--bs-primary-bg-subtle, rgba(13,110,253,0.15))"
                                                    : "var(--bs-body-bg, #ffffff)",
                                                borderColor: isSelected
                                                    ? "var(--bs-primary, #0d6efd)"
                                                    : "var(--bs-border-color, #d1d5db)",
                                                color: "var(--bs-body-color, #212529)",
                                            }}
                                        >
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span
                                                    style={{
                                                        fontWeight: 600,
                                                        color: isSelected
                                                            ? "var(--bs-primary-text-emphasis, #084298)"
                                                            : "var(--bs-body-color, #212529)",
                                                    }}
                                                >
                                                    {option.label}
                                                </span>
                                                <span
                                                    className="px-2 py-1 rounded-pill"
                                                    style={{
                                                        fontSize: "12px",
                                                        lineHeight: 1,
                                                        backgroundColor: isSelected
                                                            ? "var(--bs-primary, #0d6efd)"
                                                            : "var(--bs-secondary-bg, #e9ecef)",
                                                        color: isSelected
                                                            ? "var(--bs-white, #ffffff)"
                                                            : "var(--bs-body-color, #212529)",
                                                    }}
                                                >
                                                    {isSelected ? "Activo" : "Inactivo"}
                                                </span>
                                            </div>
                                            <div className="mt-1" style={{ fontSize: "13px", color: "var(--bs-secondary-color, #6c757d)" }}>
                                                {SETTINGS_HINTS[option.key]}
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </CollapsibleSection>
                    </div>
                </div>
            </FormDialog>

            <FormDialog
                visible={settingsInfoDialog}
                width="650px"
                title="Detalles de configuración"
                closeDialog={() => setSettingsInfoDialog(false)}
                footer={<></>}
            >
                <>
                    <p className="mb-3">
                        Las opciones de configuración de las variables permiten definir cómo se comporta cada
                        dato dentro de la conversación y cómo puede ser utilizado por el bot y el sistema.
                        Estas configuraciones ayudan a controlar la obligatoriedad, edición, eliminación
                        y persistencia de la información.
                    </p>

                    <InfoSection
                        title={"OPCIONES DE CONFIGURACIÓN DE VARIABLES"}
                        initialShowState={true}
                    >
                        <ul
                            className="ps-2 mb-3 mt-4"
                            style={{ listStylePosition: "inside" }}
                        >
                            <li>
                                <b>Requerido:</b>
                                <ul
                                    className="ps-4 mb-3"
                                    style={{ listStylePosition: "inside" }}
                                >
                                    <li>
                                        La variable debe ser completada obligatoriamente por el usuario.
                                    </li>
                                    <li>
                                        El bot no podrá continuar el flujo hasta que el valor sea proporcionado.
                                    </li>
                                </ul>
                            </li>

                            <li>
                                <b>Editable:</b>
                                <ul
                                    className="ps-4 mb-3"
                                    style={{ listStylePosition: "inside" }}
                                >
                                    <li>
                                        El valor de la variable puede modificarse una vez asignado.
                                    </li>
                                    <li>
                                        Permite corregir datos ingresados incorrectamente durante la conversación.
                                    </li>
                                </ul>
                            </li>

                            <li>
                                <b>Eliminable:</b>
                                <ul
                                    className="ps-4 mb-3"
                                    style={{ listStylePosition: "inside" }}
                                >
                                    <li>
                                        La información que contiene la variable puede ser eliminada del sistema si el usuario lo solicita.
                                    </li>
                                </ul>
                            </li>

                            <li>
                                <b>Persistente:</b>
                                <ul
                                    className="ps-4"
                                    style={{ listStylePosition: "inside" }}
                                >
                                    <li>
                                        El valor de la variable se guarda para futuras conversaciones.
                                    </li>
                                    <li>
                                        Permite al bot recordar información del usuario entre distintas interacciones,
                                        como nombres, identificadores o preferencias.
                                    </li>
                                </ul>
                            </li>
                        </ul>
                    </InfoSection>
                </>
            </FormDialog>

            <ConfirmDialog
                visible={deleteVariableDialog}
                title="Eliminar Variable"
                onClose={() => {
                    setDeleteVariableDialog(false);
                }}
                icon={<MdDelete size={40} />}
                iconBackgroundColor="red"
                confirmColor="danger"
                onConfirm={deleteSelectedVariable}
                description="¿Estás seguro que quieres eliminar la variable?"
            />
        </>
    );
}

export default TableVariables;
